import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthenticationService } from '../authentication/authentication.service';
import { BillableInformationForEmployee } from '../models/BillableInformationForEmployee';
import { ObjectWithFinancialData } from '../models/Financial/ObjectWithFinancialData';
import { OverheadCategoryMonthlyBudget } from '../models/Financial/OverheadCategoryMonthlyBudget';
import {
    BusinessDeveloperTurnoverOverview,
    BusinessDeveloperTurnoverTarget,
    DivisionTurnoverOverview,
    DivisionTurnoverTarget,
    YukiAccountingVerification,
    YukiReconciliationResult,
} from '../models/Financial/DivisionTurnoverOverview';
import {
    ResearchDevelopmentProjectOverview,
    ResearchDevelopmentTimesheetReportRow,
} from '../models/Financial/ResearchDevelopment';
import { TurnoverDataPerMonth } from '../models/Financial/TurnoverDataPerMonth';
import FinancialDetailInfoViewModel from '../models/FinancialDetailInfoViewModel';
import { HappinessScoreInformationForEmployee } from '../models/HappinessScoreInformationForEmployee';
import { InformationForFinancialData } from '../models/InformationForFinancialData';
import { OverheadCategoryWithFinancialData } from '../models/OverheadCategoryWithFinancialData';
import { ProjectBudgetOverviewReport } from '../models/Financial/ProjectBudgetOverview';
import { ProjectService } from '../projects/project.service';

@Injectable({
    providedIn: 'root',
})
export class FinancialService {
    httpOptions = {};

    constructor(private httpClient: HttpClient, private authenticationService: AuthenticationService, private projectService: ProjectService) {
        this.httpOptions = authenticationService.getHeaders();
    }

    fetchIncomeForEmployee(employeeId: string, year: number): Observable<FinancialDetailInfoViewModel> {
        return this.httpClient.get<FinancialDetailInfoViewModel>(
            `${environment.baseUrl}/financial/income/employee/${employeeId}?year=${year}`,
            this.httpOptions,
        );
    }

    fetchCostsForEmployee(employeeId: string, year: number): Observable<FinancialDetailInfoViewModel> {
        return this.httpClient.get<FinancialDetailInfoViewModel>(
            `${environment.baseUrl}/financial/costs/employee/${employeeId}?year=${year}`,
            this.httpOptions,
        );
    }

    fetchHourlyCostsForEmployee(employeeId: string, year: number): Observable<FinancialDetailInfoViewModel> {
        return this.httpClient.get<FinancialDetailInfoViewModel>(
            `${environment.baseUrl}/financial/costs/employee/${employeeId}/hourly?year=${year}`,
            this.httpOptions,
        );
    }

    fetchIncomeForProject(projectId: string, year: number): Observable<FinancialDetailInfoViewModel> {
        return this.httpClient.get<FinancialDetailInfoViewModel>(`${environment.baseUrl}/financial/income/project/${projectId}?year=${year}`, this.httpOptions);
    }

    fetchCostsForProject(projectId: string, year: number): Observable<FinancialDetailInfoViewModel> {
        return this.httpClient.get<FinancialDetailInfoViewModel>(`${environment.baseUrl}/financial/costs/project/${projectId}?year=${year}`, this.httpOptions);
    }

    fetchFinancialDataForOverheadCategories(year: number): Observable<OverheadCategoryWithFinancialData[]> {
        return this.httpClient.get<OverheadCategoryWithFinancialData[]>(`${environment.baseUrl}/financial/overhead?year=${year}`, this.httpOptions);
    }

    fetchFinancialDataForOverheadCategoryByCategoryId(categoryId: string, year: number): Observable<OverheadCategoryWithFinancialData> {
        return this.httpClient.get<OverheadCategoryWithFinancialData>(`${environment.baseUrl}/financial/overhead/${categoryId}?year=${year}`, this.httpOptions);
    }

    getAllFinancialInformationForEmployee(employeeId: string, month: number, year: number): Observable<InformationForFinancialData[]> {
        return this.httpClient.get<InformationForFinancialData[]>(
            `${environment.baseUrl}/financial/information/employee/${employeeId}?month=${month}&year=${year}`,
            this.httpOptions,
        );
    }

    getAllFinancialInformationForProject(projectId: string, month: number, year: number): Observable<InformationForFinancialData[]> {
        return this.httpClient.get<InformationForFinancialData[]>(
            `${environment.baseUrl}/financial/information/project/${projectId}?month=${month}&year=${year}`,
            this.httpOptions,
        );
    }

    getAllFinancialInformationForCategory(categoryId: string, month: number, year: number): Observable<InformationForFinancialData[]> {
        return this.httpClient.get<InformationForFinancialData[]>(
            `${environment.baseUrl}/financial/information/category/${categoryId}?month=${month}&year=${year}`,
            this.httpOptions,
        );
    }

    getBillableInformationForEmployee(year: number): Observable<BillableInformationForEmployee[]> {
        return this.httpClient.get<BillableInformationForEmployee[]>(
            `${environment.baseUrl}/financial/information/employees/billable?year=${year}`,
            this.httpOptions,
        );
    }

    getBillableInformationForEmployeeByEmployeeId(employeeId: string, year: number): Observable<BillableInformationForEmployee> {
        return this.httpClient.get<BillableInformationForEmployee>(
            `${environment.baseUrl}/financial/information/employee/${employeeId}/billable?year=${year}`,
            this.httpOptions,
        );
    }

    getHappinessScoreInformationForEmployees(year: number): Observable<HappinessScoreInformationForEmployee[]> {
        return this.httpClient.get<HappinessScoreInformationForEmployee[]>(
            `${environment.baseUrl}/financial/information/employees/happiness-score?year=${year}`,
            this.httpOptions,
        );
    }

    getHappinessScoreInformationForEmployeeByEmployeeId(employeeId: string, year: number): Observable<HappinessScoreInformationForEmployee> {
        return this.httpClient.get<HappinessScoreInformationForEmployee>(
            `${environment.baseUrl}/financial/information/employee/${employeeId}/happiness-score?year=${year}`,
            this.httpOptions,
        );
    }

    getEmployeesFinancialData(year: number) {
        return this.httpClient.get<ObjectWithFinancialData[]>(`${environment.baseUrl}/financial/employees/financial-data?year=${year}`, this.httpOptions);
    }

    getProjectsFinancialData(year: number) {
        return this.httpClient.get<ObjectWithFinancialData[]>(`${environment.baseUrl}/financial/projects/financial-data?year=${year}`, this.httpOptions);
    }

    getProjectsFinancialDataAllTime(currentOnly: boolean) {
        return this.httpClient.get<ObjectWithFinancialData[]>(
            `${environment.baseUrl}/financial/projects/financial-data/all-time?currentOnly=${currentOnly}`,
            this.httpOptions
        );
    }

    getResearchDevelopmentProjectOverview(year: number): Observable<ResearchDevelopmentProjectOverview[]> {
        return this.httpClient.get<ResearchDevelopmentProjectOverview[]>(
            `${environment.baseUrl}/financial/research-development/projects?year=${year}`,
            this.httpOptions,
        );
    }

    getResearchDevelopmentTimesheetReport(year: number): Observable<ResearchDevelopmentTimesheetReportRow[]> {
        return this.httpClient.get<ResearchDevelopmentTimesheetReportRow[]>(
            `${environment.baseUrl}/financial/research-development/timesheets?year=${year}`,
            this.httpOptions,
        );
    }

    getOverheadCategoriesFinancialOverviewData(year: number, includeHidden = false) {
        return this.httpClient.get<ObjectWithFinancialData[]>(
            `${environment.baseUrl}/financial/overhead-categories/financial-data?year=${year}&includeHidden=${includeHidden}`,
            this.httpOptions,
        );
    }

    getTurnoverData(year: number): Observable<TurnoverDataPerMonth[]> {
        return this.httpClient.get<TurnoverDataPerMonth[]>(`${environment.baseUrl}/financial/turnover?year=${year}`, this.httpOptions);
    }

    getOverheadCategoryMonthlyBudgets(categoryId: string, year: number): Observable<OverheadCategoryMonthlyBudget[]> {
        return this.httpClient.get<OverheadCategoryMonthlyBudget[]>(
            `${environment.baseUrl}/financial/overhead-categories/${categoryId}/budgets?year=${year}`,
            this.httpOptions,
        );
    }

    upsertOverheadCategoryMonthlyBudget(
        categoryId: string,
        payload: { year: number; month: number; budget: number },
    ): Observable<OverheadCategoryMonthlyBudget> {
        return this.httpClient.put<OverheadCategoryMonthlyBudget>(
            `${environment.baseUrl}/financial/overhead-categories/${categoryId}/budgets`,
            payload,
            this.httpOptions,
        );
    }

    upsertOverheadCategoryMonthlyBudgets(
        categoryId: string,
        payload: Array<{ year: number; month: number; budget: number }>,
    ): Observable<OverheadCategoryMonthlyBudget[]> {
        return this.httpClient.put<OverheadCategoryMonthlyBudget[]>(
            `${environment.baseUrl}/financial/overhead-categories/${categoryId}/budgets/bulk`,
            payload,
            this.httpOptions,
        );
    }

    deleteOverheadCategoryMonthlyBudget(categoryId: string, year: number, month: number): Observable<void> {
        return this.httpClient.delete<void>(
            `${environment.baseUrl}/financial/overhead-categories/${categoryId}/budgets?year=${year}&month=${month}`,
            this.httpOptions,
        );
    }

    deleteOverheadCategoryMonthlyBudgetsForYear(categoryId: string, year: number): Observable<void> {
        return this.httpClient.delete<void>(
            `${environment.baseUrl}/financial/overhead-categories/${categoryId}/budgets/year?year=${year}`,
            this.httpOptions,
        );
    }

    getTimesheetsForProject(projectId: string, year: number, month: number | null = null): Observable<any[]> {
        let url = `${environment.baseUrl}/timesheets/project/${projectId}?year=${year}`;
        if (month !== null) {
            url += `&month=${month + 1}`; // Backend expects 1-12, JavaScript Date uses 0-11
        }
        return this.httpClient.get<any[]>(url, this.httpOptions);
    }

    getBudgetsMonthly(year: number): Observable<any[]> {
        return this.httpClient.get<any[]>(`${environment.baseUrl}/financial/budgets/monthly?year=${year}`, this.httpOptions);
    }

    getContractsMonthlyOverview(year: number): Observable<any> {
        return this.httpClient.get<any>(`${environment.baseUrl}/financial/contracts/monthly-overview?year=${year}`, this.httpOptions);
    }

    getProjectBudgetsByAccount(): Observable<ProjectBudgetOverviewReport> {
        return this.httpClient.get<ProjectBudgetOverviewReport>(`${environment.baseUrl}/financial/project-budgets-by-account`, this.httpOptions);
    }

    getYukiAccountsForBudget(budgetId: string): Observable<string[]> {
        return this.httpClient.get<string[]>(`${environment.baseUrl}/financial/budgets/${budgetId}/yuki-accounts`, this.httpOptions);
    }

    updateYukiAccountsForBudget(budgetId: string, accounts: string[]): Observable<string[]> {
        return this.httpClient.put<string[]>(`${environment.baseUrl}/financial/budgets/${budgetId}/yuki-accounts`, accounts, this.httpOptions);
    }

    // ── Division Turnover Targets ──────────────────────────────────────────────

    getDivisionTurnoverOverview(year: number): Observable<DivisionTurnoverOverview[]> {
        return this.httpClient.get<DivisionTurnoverOverview[]>(
            `${environment.baseUrl}/financial/division-turnover/overview?year=${year}`, this.httpOptions);
    }

    getDivisionTurnoverTargets(year: number): Observable<DivisionTurnoverTarget[]> {
        return this.httpClient.get<DivisionTurnoverTarget[]>(
            `${environment.baseUrl}/financial/division-turnover/targets?year=${year}`, this.httpOptions);
    }

    upsertDivisionTurnoverTargets(targets: DivisionTurnoverTarget[]): Observable<DivisionTurnoverTarget[]> {
        return this.httpClient.put<DivisionTurnoverTarget[]>(
            `${environment.baseUrl}/financial/division-turnover/targets`, targets, this.httpOptions);
    }

    deleteDivisionTurnoverTargets(year: number): Observable<void> {
        return this.httpClient.delete<void>(
            `${environment.baseUrl}/financial/division-turnover/targets?year=${year}`, this.httpOptions);
    }

    getBusinessDeveloperTurnoverOverview(year: number): Observable<BusinessDeveloperTurnoverOverview[]> {
        return this.httpClient.get<BusinessDeveloperTurnoverOverview[]>(
            `${environment.baseUrl}/financial/business-developer-turnover/overview?year=${year}`, this.httpOptions);
    }

    getBusinessDeveloperTurnoverTargets(year: number): Observable<BusinessDeveloperTurnoverTarget[]> {
        return this.httpClient.get<BusinessDeveloperTurnoverTarget[]>(
            `${environment.baseUrl}/financial/business-developer-turnover/targets?year=${year}`, this.httpOptions);
    }

    upsertBusinessDeveloperTurnoverTargets(targets: BusinessDeveloperTurnoverTarget[]): Observable<BusinessDeveloperTurnoverTarget[]> {
        return this.httpClient.put<BusinessDeveloperTurnoverTarget[]>(
            `${environment.baseUrl}/financial/business-developer-turnover/targets`, targets, this.httpOptions);
    }

    deleteBusinessDeveloperTurnoverTargets(year: number): Observable<void> {
        return this.httpClient.delete<void>(
            `${environment.baseUrl}/financial/business-developer-turnover/targets?year=${year}`, this.httpOptions);
    }

    sendTargetsOverviewEmail(year: number, email: string): Observable<void> {
        return this.httpClient.post<void>(
            `${environment.baseUrl}/financial/division-turnover/send-overview-email?year=${year}&email=${encodeURIComponent(email)}`,
            null, this.httpOptions);
    }

    reconcileWithYuki(year: number): Observable<YukiReconciliationResult> {
        return this.httpClient.get<YukiReconciliationResult>(
            `${environment.baseUrl}/financial/division-turnover/reconcile?year=${year}`, this.httpOptions);
    }

    verifyWithYukiAccounting(year: number): Observable<YukiAccountingVerification> {
        return this.httpClient.get<YukiAccountingVerification>(
            `${environment.baseUrl}/financial/division-turnover/verify-yuki?year=${year}`, this.httpOptions);
    }

    analyzeDifference(body: any): Observable<{ analysis: string }> {
        return this.httpClient.post<{ analysis: string }>(
            `${environment.baseUrl}/financial/division-turnover/analyze-difference`, body, this.httpOptions);
    }

    analyzeEmployeeFinancials(year: number, employees: any[]): Observable<{ analysis: string }> {
        return this.httpClient.post<{ analysis: string }>(
            `${environment.baseUrl}/financial/employees/analyze`,
            { year, employees }, this.httpOptions);
    }

    analyzeProjectFinancials(year: number, projects: any[]): Observable<{ analysis: string }> {
        return this.httpClient.post<{ analysis: string }>(
            `${environment.baseUrl}/financial/projects/analyze`,
            { year, projects }, this.httpOptions);
    }

    sendEmployeeFinancialEmail(year: number, email: string): Observable<void> {
        return this.httpClient.post<void>(
            `${environment.baseUrl}/financial/employees/send-overview-email?year=${year}&email=${encodeURIComponent(email)}`,
            null, this.httpOptions);
    }

    sendProjectFinancialEmail(year: number, email: string): Observable<void> {
        return this.httpClient.post<void>(
            `${environment.baseUrl}/financial/projects/send-overview-email?year=${year}&email=${encodeURIComponent(email)}`,
            null, this.httpOptions);
    }

    // ── Targets Data Source Setting ─────────────────────────────────────────────

    getTargetsDataSource(): Observable<{ dataSource: string }> {
        return this.httpClient.get<{ dataSource: string }>(
            `${environment.baseUrl}/settings/targets-data-source`, this.httpOptions);
    }

    updateTargetsDataSource(dataSource: string): Observable<void> {
        return this.httpClient.put<void>(
            `${environment.baseUrl}/settings/targets-data-source`,
            { dataSource }, this.httpOptions);
    }
}
